import { useCallback, useMemo, useState } from "react";
import {
  BucketSingleInfoCards,
  BucketTotals,
  PDFReportClientInfoItem,
  PDFReportValueType,
} from "helpers/types";
import { BucketSummaryEnum, BucketTwrFormula } from "constants/index";
import {
  StrategyReportSingleValueCardDescriptions,
  StrategyReportSingleValueCardLabels,
} from "helpers/constants";
import { useStrategyReportValue } from "hooks/useReportStorage";
import {
  summaryIncomeTile,
  summaryMarginInterestTile,
  summaryTileDimmed,
  summaryTileValue,
  summaryValueCardFooters,
  withLongValueReason,
} from "../summaryCardFooters";
import { STRATEGY_REPORT_DEFAULTS } from "./strategyReportDefaults";
import {
  AggregateReturn,
  LongValueSummaryCards,
  longValueCards,
  summaryValueDateNotes,
  timeWeightedReturnFigure,
} from "./summaryFigures";
import { pdfFigureValue } from "./pdfExport";

/**
 * The Summary row: its six tiles (the backend's card_1..card_6), the scope
 * drop-down above them, the time-weighted return formula the gear picks, and
 * the run's bucket totals every scope is read from.
 */
export const useSummaryCards = () => {
  const [startingValue, setStartingValue] = useStrategyReportValue(
    "starting_value_strategy",
    STRATEGY_REPORT_DEFAULTS.STARTING_VALUE,
  );
  const [endingValue, setEndingValue] = useStrategyReportValue(
    "ending_value_strategy",
    STRATEGY_REPORT_DEFAULTS.ENDING_VALUE,
  );
  const [netChange, setNetChange] = useStrategyReportValue(
    "net_change_strategy",
    STRATEGY_REPORT_DEFAULTS.NET_CHANGE,
  );
  const [timeWeightedReturn, setTimeWeightedReturn] = useStrategyReportValue(
    "time_weighted_return_strategy",
    STRATEGY_REPORT_DEFAULTS.TIME_WEIGHTED_RETURN,
  );
  const [incomeReceived, setIncomeReceived] = useStrategyReportValue(
    "income_received_strategy",
    STRATEGY_REPORT_DEFAULTS.INCOME_RECEIVED,
  );
  const [marginInterest, setMarginInterest] = useStrategyReportValue(
    "margin_interest_strategy",
    STRATEGY_REPORT_DEFAULTS.MARGIN_INTEREST,
  );
  // The inputs the six figures above were calculated for, stored beside
  // them -- see downloadPdfAvailability.
  const [pdfReportInputsKey, setPdfReportInputsKey] = useStrategyReportValue(
    "summary_inputs_key_strategy",
    "",
  );
  const [bucketTotals, setBucketTotals] = useState<BucketTotals | null>(null);
  // Why the selected scope has no time-weighted return, when it has none.
  //
  // Plain state rather than `useStrategyReportValue`, and that is the same
  // lifetime every other backend-sent sentence on this page has: it belongs
  // to ONE run's payload, which `bucketTotals` above also holds in state, so
  // persisting the reason past the run it describes would explain a figure
  // this session no longer has. The card degrades honestly when the report
  // unmounts and takes both with it -- the stored value is cleared to "" in
  // applyBucketCards below, so what comes back is a bare N/A rather than the
  // 0.00% this reason exists to stop showing.
  const [timeWeightedReturnReason, setTimeWeightedReturnReason] = useState<
    string | undefined
  >(undefined);
  // The sentence the three value cards carry when the Long Value scope could
  // not be given them (operator, task 3636), held on exactly the terms the
  // return's reason above is. The three stored values are cleared to "" beside
  // it in applyBucketCards, so what a remount brings back is a bare N/A rather
  // than the previous run's opening balance.
  const [valueCardsReason, setValueCardsReason] = useState<string | undefined>(
    undefined,
  );
  // Where the two value figures were measured, when the scope's bucket did
  // not cover the whole period (operator, task 3730) -- summaryValueDateNotes
  // builds the sentences and says when there are any.
  //
  // Session-only on the same terms as the two reasons above, and unlike
  // those it sits beside a figure that IS there, so a stale one would misdate
  // a number the reader can read.
  const [startingValueDateNote, setStartingValueDateNote] = useState<
    string | undefined
  >(undefined);
  const [endingValueDateNote, setEndingValueDateNote] = useState<
    string | undefined
  >(undefined);
  const [selectedSummary, setSelectedSummary] = useState<BucketSummaryEnum>(
    BucketSummaryEnum.BUCKET_1,
  );
  // The portfolio's aggregate return AS THIS RUN REPORTED IT, held for the
  // Long Value scope's card_4 (task 3542).
  //
  // A second holder of a return the Diversify card already stores, and it
  // has to be, for two reasons the stored one cannot serve. Its LIFETIME is
  // this payload's: reading a persisted figure here would credit Long Value
  // with the return of a run whose buckets are no longer on screen -- the
  // same rule the refusal beside it follows, and the same one
  // `timeWeightedReturnReason` above keeps. And its TIMING is this tick's: the
  // Diversify card stores its own figure AFTER the summary cards are applied,
  // so a card built from state would be built from the PREVIOUS run's
  // aggregate every time. The run path passes the payload's own value
  // straight through and stores it here for the drop-down changes that come
  // later, which have no payload to read.
  const [bucketAggregateReturn, setBucketAggregateReturn] =
    useState<AggregateReturn>({ value: null });
  // Which time-weighted return produced the figures ON SCREEN, which is what
  // the gear shows. Session-only on purpose: it is a question the reader asks
  // of THIS run, so it is not written to redux, storage or the account -- a
  // fresh mount is back on DAILY_WEIGHT_ADJUSTMENT, the default.
  //
  // Set from the backend's echo of the run that succeeded, never from the
  // pick. A run can fail -- a rejected job, a network error -- and the cards,
  // the chart and the Excel link then still hold the PREVIOUS formula's
  // results. A radio that had moved on the click would be naming a formula
  // nothing on screen was calculated by, which is the one thing a selector
  // over two near-identical figures must never do.
  const [twrFormula, setTwrFormula] = useState<BucketTwrFormula>(
    BucketTwrFormula.DAILY_WEIGHT_ADJUSTMENT,
  );

  // Which footers the three value tiles carry, which follows the scope on
  // screen -- see summaryCardFooters for why all three change together on
  // Net Value and why the mapping is not inlined here.
  const summaryFooters = useMemo(
    () => summaryValueCardFooters(selectedSummary),
    [selectedSummary],
  );
  // Whether a given Summary tile recedes on the scope now selected (task
  // 3580). ASKED PER TILE rather than once for the row, because the answer
  // differs between them: Income Received and Margin Interest do not move
  // with Net Value and recede, while the Time-Wgt Return does move with it
  // (task 3637) and stays at full strength. Every one of the three asks, so
  // the membership lives in summaryCardFooters where a test can reach it and
  // not in which JSX props happen to be present.
  // Read straight rather than memoised: it is an enum comparison and a Set
  // lookup, and the boolean is what the cards are compared on anyway.
  const tileDimmed = (tile: StrategyReportSingleValueCardLabels) =>
    summaryTileDimmed(selectedSummary, tile);
  // Whether the reader has typed their own figure into Income Received or
  // Margin Interest on the scope now on screen (operator, task 3781). Net
  // Value withholds both tiles' figures but still takes an override, and a
  // typed figure undims the tile and prints on paper. Session-only and
  // cleared by every `applyBucketCards` -- a scope switch or a re-run --
  // because that call overwrites the stored figure the flag vouches for,
  // exactly as it does every other override on this row.
  const [customSummaryTiles, setCustomSummaryTiles] = useState({
    income: false,
    marginInterest: false,
  });
  // Income Received and Margin Interest need MORE than tileDimmed can say
  // (task 3639): on some scopes they withhold the FIGURE rather than only
  // receding, and each state reads its own dimming back off tileDimmed's
  // mapping so the membership is still single-homed there. Both are resolved
  // once here and read by the live card and the PDF payload alike, which is
  // what keeps the paper copy from printing a figure the screen withheld.
  //
  // MEMOISED, where tileDimmed is read straight: each of these is a fresh
  // object every render, and the PDF payload below depends on them -- an
  // unmemoised state would rebuild that whole payload on every keystroke
  // anywhere on the page.
  //
  // On Long Value both tiles also carry the value cards' reason when the run
  // sent no `long_value` entry (operator, task 4700): their figures come off
  // that entry alone, so the tile is N/A with the same re-run sentence.
  const incomeTile = useMemo(
    () =>
      withLongValueReason(
        summaryIncomeTile(selectedSummary, customSummaryTiles.income),
        selectedSummary,
        valueCardsReason,
      ),
    [selectedSummary, customSummaryTiles.income, valueCardsReason],
  );
  const marginInterestTile = useMemo(
    () =>
      withLongValueReason(
        summaryMarginInterestTile(
          selectedSummary,
          customSummaryTiles.marginInterest,
        ),
        selectedSummary,
        valueCardsReason,
      ),
    [selectedSummary, customSummaryTiles.marginInterest, valueCardsReason],
  );
  // The two tiles' override handlers: the stored figure takes the number, and
  // the flag above says it is the reader's -- which only Net Value reads.
  // Emptying the box drops the flag and the tile goes back to its withheld
  // N/A; the stored figure is left alone, since the withheld state never
  // shows it and the next scope switch or run overwrites it anyway.
  const handleIncomeChange = useCallback(
    (value: number) => {
      setIncomeReceived(value);
      setCustomSummaryTiles((tiles) => ({ ...tiles, income: true }));
    },
    [setIncomeReceived],
  );
  const handleMarginInterestChange = useCallback(
    (value: number) => {
      setMarginInterest(value);
      setCustomSummaryTiles((tiles) => ({ ...tiles, marginInterest: true }));
    },
    [setMarginInterest],
  );
  const clearIncomeCustom = useCallback(
    () => setCustomSummaryTiles((tiles) => ({ ...tiles, income: false })),
    [],
  );
  const clearMarginInterestCustom = useCallback(
    () =>
      setCustomSummaryTiles((tiles) => ({ ...tiles, marginInterest: false })),
    [],
  );
  // What each of the two actually shows: its own stored figure, or the seed
  // or empty sentinel its state substitutes for one.
  const incomeFigure = summaryTileValue(incomeTile, incomeReceived);
  const marginInterestFigure = summaryTileValue(
    marginInterestTile,
    marginInterest,
  );
  // Bucket 1's and Bucket 3's figures are their dividends ALONE, so their
  // tiles say so rather than carrying the standing "Divs & Option Premium"
  // that names a term they do not contain (tasks 3639, 3790). Resolved here
  // for the same reason the figure is: the card and the PDF payload each
  // render it, and they must not disagree.
  const incomeFooter =
    incomeTile.footer ??
    StrategyReportSingleValueCardDescriptions.INCOME_RECEIVED;

  const applyBucketCards = useCallback(
    (
      totals: BucketTotals,
      summary: BucketSummaryEnum,
      // Passed rather than read from `bucketAggregateReturn` above: the run
      // path applies the cards before the state holding this has settled, so
      // the caller hands over the value it is also storing. Long Value is the
      // only scope that reads it.
      aggregateReturn: AggregateReturn,
      // The gear's pick, passed rather than read from `twrFormula` above for
      // the reason `aggregateReturn` is: the pick handler applies the cards
      // in the same tick it sets the state, when this callback would still
      // close over the previous value. Every other caller hands over the
      // state value it is already holding.
      formula: BucketTwrFormula,
    ) => {
      // Long Value reads its value, income and margin cards off the
      // backend's `long_value` entry and fills its RETURN from the portfolio
      // aggregate -- longValueCards says why. Every other scope, Net Value
      // included, reads the entry it was sent throughout.
      //
      // Split out rather than folded into one expression because only this
      // scope can refuse the value cards, and the refusal has to survive the
      // widening to the shared type below: `totals[summary]` has no such
      // field, so reading it off `cards` would not narrow.
      let valuesUnavailable: string | undefined;
      // The two value tiles' date notes (operator, task 3730), resolved here
      // for the reason the refusal beside it is: only the backend's own entry
      // carries the dates, and LongValueSummaryCards has no such fields, so
      // reading them off `cards` after the widening below would not compile
      // -- and Long Value has no single date to state anyway. Net Value takes
      // the same branch as a numbered bucket and needs no test of its own:
      // the backend emits the keys for "1"/"2"/"3" alone (types.ts), so its
      // entry yields no note through the very same read.
      let dateNotes: ReturnType<typeof summaryValueDateNotes> = {};
      let cards: BucketSingleInfoCards | LongValueSummaryCards | undefined;
      if (summary === BucketSummaryEnum.LONG_VALUE) {
        const longCards = longValueCards(totals, aggregateReturn);
        if (!longCards) return;
        cards = longCards;
        valuesUnavailable = longCards.values_unavailable;
      } else {
        const bucketCards = totals[summary]?.single_info_cards ?? undefined;
        cards = bucketCards;
        dateNotes = summaryValueDateNotes(bucketCards);
      }
      if (!cards) return;
      setStartingValue(cards.card_1);
      setEndingValue(cards.card_2);
      setNetChange(cards.card_3);
      setValueCardsReason(valuesUnavailable);
      // Set on EVERY call, like the reason above: a scope with no dates has
      // to CLEAR the previous scope's notes, or the drop-down leaves one
      // bucket's late-start date hanging off another bucket's figure.
      setStartingValueDateNote(dateNotes.startingValue);
      setEndingValueDateNote(dateNotes.endingValue);
      // The RETURN card is the one figure on this scope the gear moves, and
      // it moves with no re-run because every formula's answer arrived with
      // the run (operator, task 3764). Long Value takes the fallback inside
      // timeWeightedReturnFigure -- longValueCards carries no per-formula
      // map, because a cross-bucket total is not a BucketTwrFormula
      // measurement and reads the same under either pick.
      const twr = timeWeightedReturnFigure(cards, formula);
      setTimeWeightedReturn(twr.value);
      setTimeWeightedReturnReason(twr.unavailableReason);
      setIncomeReceived(cards.card_5);
      setMarginInterest(cards.card_6);
      setCustomSummaryTiles({ income: false, marginInterest: false });
    },
    [
      setStartingValue,
      setEndingValue,
      setNetChange,
      setTimeWeightedReturn,
      setIncomeReceived,
      setMarginInterest,
    ],
  );

  // Applies a completed run's bucket totals. `runFormula` is the backend's
  // echo of the formula the run was produced under, and `inputsKey` the key
  // of the inputs the run STARTED with.
  const applyRun = useCallback(
    (
      totals: BucketTotals,
      runFormula: BucketTwrFormula | undefined,
      inputsKey: string,
    ) => {
      // The run's own formula, which is the one its WORKBOOK was written
      // under -- read off the result rather than off the pick, so a job
      // served from cache under a different formula than the one just asked
      // for is named correctly and the Excel button is not mislabelled by the
      // gear above it.
      //
      // Since task 3764 the gear no longer decides which figure the cards
      // show -- every formula's figure arrived with this run and the pick
      // chooses between them -- so this is the one place the two can
      // disagree, and applyBucketCards below is handed the SAME formula to
      // keep the card showing what the gear names.
      const formula = runFormula ?? twrFormula;
      setTwrFormula(formula);
      setBucketTotals(totals);
      // Long Value's card_4 comes off the LONG VALUE SCOPE'S OWN CARD
      // (operator, task 3603). The backend measures a cross-bucket return
      // with the same engine every bucket's figure comes from, and puts it
      // where every other scope's return already sits -- so this reads one
      // source instead of lifting a figure out of the Diversify card, which
      // is a different card measuring a different thing. (The Diversify
      // card's `portfolio_aggregate_return` is that card's own row; its
      // sibling `overall_diversified_performance` was never a candidate --
      // task 3573 narrowed it to Buckets 2 and 3.)
      //
      // Read off THIS payload rather than off state, which still holds the
      // previous run's this tick (see bucketAggregateReturn). Stored
      // alongside for the drop-down changes that follow, which have no
      // payload to read.
      const longValueEntry =
        totals[BucketSummaryEnum.LONG_VALUE]?.single_info_cards;
      const runAggregate: AggregateReturn = {
        value: longValueEntry?.card_4 ?? null,
        unavailable: longValueEntry?.card_4_unavailable ?? undefined,
      };
      setBucketAggregateReturn(runAggregate);
      // A summary with no successful data is absent from totals.
      const summaryToShow = totals[selectedSummary]
        ? selectedSummary
        : Object.values(BucketSummaryEnum).find((summary) => totals[summary]);
      if (summaryToShow) {
        setSelectedSummary(summaryToShow);
        applyBucketCards(totals, summaryToShow, runAggregate, formula);
        // The key this run STARTED with, not the one on screen now: a run
        // that finishes after a filter change has filled the cards with
        // the old inputs' figures, and must not unlock the PDF for the new.
        setPdfReportInputsKey(inputsKey);
      }
    },
    [twrFormula, selectedSummary, applyBucketCards, setPdfReportInputsKey],
  );

  // A change of report inputs retires the run on screen.
  const resetForNewInputs = useCallback(() => {
    setBucketTotals(null);
  }, []);

  const handleSummaryChange = useCallback(
    (summary: BucketSummaryEnum) => {
      setSelectedSummary(summary);
      if (bucketTotals)
        applyBucketCards(
          bucketTotals,
          summary,
          bucketAggregateReturn,
          twrFormula,
        );
    },
    [bucketTotals, applyBucketCards, bucketAggregateReturn, twrFormula],
  );

  // Picking a formula RE-READS the run, it does not re-run it (operator,
  // task 3764). Every formula's figure and every formula's chart line
  // travel on the one response, so the pick is a render and costs no job,
  // no poll and no wait.
  //
  // The two that move are the Time-Wgt Return card and the bucket's chart
  // line, and they move TOGETHER: the chart's last point is 100 plus that
  // formula's own card_4 (bucket_performance_series), so switching one
  // without the other would put a chart and a card that disagree side by
  // side. Everything else on the page -- the workbook behind the Excel
  // button, the Diversify card's aggregate rows -- stays as the run
  // produced it, and the next Calculate is what moves those, since the
  // pick is still what the request sends.
  const handleTwrFormulaChange = useCallback(
    (formula: BucketTwrFormula) => {
      setTwrFormula(formula);
      // Applied HERE with the pick handed over explicitly rather than left
      // to an effect on `twrFormula`: the state set above has not settled
      // this tick, so an effect would re-render the cards a frame later and
      // this callback would read the previous formula.
      if (bucketTotals)
        applyBucketCards(
          bucketTotals,
          selectedSummary,
          bucketAggregateReturn,
          formula,
        );
    },
    [bucketTotals, selectedSummary, bucketAggregateReturn, applyBucketCards],
  );

  // The six tiles as the client's PDF prints them.
  const pdfClientInfo = useMemo(
    (): PDFReportClientInfoItem[] => [
      {
        label: StrategyReportSingleValueCardLabels.STARTING_VALUE,
        value: pdfFigureValue(startingValue, valueCardsReason),
        type: PDFReportValueType.CURRENCY,
        description: summaryFooters.startingValue,
      },
      {
        label: StrategyReportSingleValueCardLabels.ENDING_VALUE_PDF,
        value: pdfFigureValue(endingValue, valueCardsReason),
        type: PDFReportValueType.CURRENCY,
        description: summaryFooters.endingValue,
      },
      {
        label: StrategyReportSingleValueCardLabels.NET_CHANGE,
        value: pdfFigureValue(netChange, valueCardsReason),
        type: PDFReportValueType.CURRENCY,
        description: summaryFooters.netChange,
      },
      {
        label: StrategyReportSingleValueCardLabels.TIME_WEIGHTED_RETURN_PDF,
        value: pdfFigureValue(timeWeightedReturn, timeWeightedReturnReason),
        type: PDFReportValueType.PERCENTAGE,
        description:
          StrategyReportSingleValueCardDescriptions.TIME_WEIGHTED_RETURN,
      },
      {
        label: StrategyReportSingleValueCardLabels.INCOME_RECEIVED,
        // The SCREEN's figure, not the stored one (task 3639): a scope that
        // withholds this tile withholds it on paper too, where a withheld
        // figure becomes "N/A".
        value: pdfFigureValue(incomeFigure, incomeTile.unavailableReason),
        type: PDFReportValueType.CURRENCY,
        description: incomeFooter,
      },
      {
        label: StrategyReportSingleValueCardLabels.MARGIN_INTEREST,
        value: pdfFigureValue(
          marginInterestFigure,
          marginInterestTile.unavailableReason,
        ),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.MARGIN_INTEREST,
      },
    ],
    [
      summaryFooters,
      startingValue,
      endingValue,
      netChange,
      valueCardsReason,
      timeWeightedReturn,
      timeWeightedReturnReason,
      incomeFigure,
      incomeFooter,
      incomeTile,
      marginInterestFigure,
      marginInterestTile,
    ],
  );

  return {
    startingValue,
    setStartingValue,
    endingValue,
    setEndingValue,
    netChange,
    setNetChange,
    timeWeightedReturn,
    setTimeWeightedReturn,
    timeWeightedReturnReason,
    valueCardsReason,
    startingValueDateNote,
    endingValueDateNote,
    summaryFooters,
    tileDimmed,
    incomeTile,
    incomeFigure,
    incomeFooter,
    handleIncomeChange,
    clearIncomeCustom,
    marginInterestTile,
    marginInterestFigure,
    handleMarginInterestChange,
    clearMarginInterestCustom,
    pdfReportInputsKey,
    bucketTotals,
    selectedSummary,
    handleSummaryChange,
    twrFormula,
    handleTwrFormulaChange,
    pdfClientInfo,
    applyRun,
    resetForNewInputs,
  };
};

export type SummaryCardsState = ReturnType<typeof useSummaryCards>;
